import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import moment from 'moment';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, Radio } from '@mui/material';
import {
    AddCircleBlue,
    CreateChancel,
    DateCalendarForm,
    EditWithIcon,
    ErrMessage,
    Input,
    Loader,
    MinLoader,
    NonBillable,
    NoYet,
    NumericFormatCustom,
    SelectAutocompleteMultiple,
    SelectAutocompletePagination,
    SwitchCustomized,
    TextRow,
} from 'components';
import { frequencyEnumsConvert, frequencyList } from './constants';
import { Colors, FindLoad, FindSuccessItem, Images, renderStaffName, staffStatusEnums, useModal } from 'utils';
import { clientActions, fundingSourceActions, httpRequestsOnLoadActions, httpRequestsOnSuccessActions } from 'store';
import { authService } from '../../../store/client/client.service';
import { createClientStyle } from './styles';
import { enrollmentTypeEnums } from '../clientSingle/core/constants';
import { v4 as uuidv4 } from 'uuid';
import { MAX_AUTHORIZATION_ID_LENGTH } from '../../../utils/constants';
import { NO_PRIOR } from '../../../utils/enumValues';

const GET_FS_SERVICE = 'GET_FUNDING_SOURCE_SERVICE_BY_ID';
const GET_FS_CHARGE_RATES = 'GET_FS_CHARGE_RATES';
export const AddClientAuthorization = ({ item, clone }) => {
    const { fundingSourceServices, clientEnrollmentPayers } = useSelector((state) => ({
        // fSelect: state.fundingSource.fSelect.funders,
        fundingSourceServices: state.fundingSource.fundingSourceServices,
        clientEnrollmentPayers: state.client.clientEnrollmentPayers,
    }));

    const { close } = useModal();
    const client = useSelector((state) => state.client.clientItemInfo);
    const dispatch = useDispatch();
    const ACTION_TYPE = item && !clone ? 'EDIT_CLIENT_AUTHORIZATION' : 'ADD_CLIENT_AUTHORIZATION';
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const loadModal = FindLoad(GET_FS_CHARGE_RATES);
    const [expanded, setExpanded] = useState(0);
    const [chargeRatesList, setChargeRateLists] = useState([]);
    const [uniqueId, setUniqueId] = useState(null);
    const {
        handleSubmit,
        control,
        clearErrors,
        watch,
        reset,
        setValue,
        setError,
        trigger,
        formState: { errors },
    } = useForm({
        defaultValues: {
            payerId: null,
            renderingProvider: null,
            authorizationId: null,
            authStartDate: null,
            authEndDate: null,
            billable: true,
            authorizedServices: [
                {
                    serviceId: null,
                    totalUnits: null,
                    apptVisible: true,
                    startDate: null,
                    endDate: null,
                    chargeRateList: [],
                    chargeRateIds: [],
                    frequencyLimit: {
                        limit: null,
                        mode: null,
                    },
                },
            ],
        },
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'authorizedServices',
    });
    const [checkModifiersLoader, setCheckModifiersLoader] = useState(false);
    const classes = createClientStyle();
    const last = watch('authorizedServices')[watch('authorizedServices')?.length - 1];
    const checkAllFilled =
        watch('payerId') &&
        watch('authorizationId') &&
        last?.serviceId &&
        last?.totalUnits &&
        last?.startDate &&
        last?.endDate &&
        last?.frequencyLimit?.mode &&
        last?.frequencyLimit?.mode === frequencyEnumsConvert.ENTIRE_TERM
            ? true
            : last?.frequencyLimit?.limit;

    useEffect(() => {
        if (!item && !clone && client?.renderingProviderId) {
            setValue('renderingProvider', client?.renderingProviderId);
        }
    }, []);

    useEffect(() => {
        const checkUniqueSet = clone ? true : !item;
        if (checkUniqueSet) {
            setUniqueId(uuidv4());
        }
    }, []);

    useEffect(() => {
        if (watch(`payerId`)) {
            dispatch(fundingSourceActions.getFoundingSourceServiceById(watch(`payerId`), { status: 'ACTIVE' }));
        }
    }, [watch(`payerId`)]);

    useEffect(() => {
        if (success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();
        }
    }, [success]);

    const handleCreate = (data) => {
        // if(!watch('renderingProvider')) {
        //     setError('renderingProvider', {
        //         type: 'required',
        //         message: 'Rendering Provider is required.',
        //     });
        //     return
        // }

        const body = {
            authorizedServices: data?.authorizedServices.map(({ chargeRateList, ...rest }) => {
                if (item && !clone) {
                    return {
                        ...rest,
                        payerId: data.payerId,
                        renderingProvider: data.renderingProvider,
                        authorizationId: data.authorizationId,
                        billable: data.billable,
                        startDate: item?.startDate !== watch('authStartDate') ? watch('authStartDate') : rest.startDate,
                        endDate: item?.endDate !== watch('authEndDate') ? watch('authEndDate') : rest.endDate,
                        frequencyLimit: {
                            limit:
                                rest.frequencyLimit?.mode === frequencyEnumsConvert.ENTIRE_TERM
                                    ? rest.totalUnits
                                    : rest.frequencyLimit?.limit,
                            mode: rest.frequencyLimit?.mode,
                            _id: rest.frequencyLimit?._id ? rest.frequencyLimit?._id : null,
                        },
                    };
                } else {
                    return {
                        ...rest,
                        authorizationUniqueId: uniqueId,
                        payerId: data.payerId,
                        renderingProvider: data.renderingProvider,
                        authorizationId: data.authorizationId,
                        billable: data.billable,
                        frequencyLimit: {
                            limit:
                                rest.frequencyLimit?.mode === frequencyEnumsConvert.ENTIRE_TERM
                                    ? rest.totalUnits
                                    : rest.frequencyLimit?.limit,
                            mode: rest.frequencyLimit?.mode,
                            _id: rest.frequencyLimit?._id,
                        },
                    };
                }
            }),
        };
        if (item && !clone) {
            dispatch(clientActions.editClientAuthorization(client?.id, body));
        } else {
            dispatch(clientActions.addClientAuthorization(client?.id, body));
        }
    };

    const handleInvalid = (formErrors) => {
        // On submit, expand the first service row that has validation errors so its
        // field-level messages are visible instead of being hidden in a collapsed accordion.
        const serviceErrors = formErrors?.authorizedServices;
        if (Array.isArray(serviceErrors)) {
            const firstErrorIndex = serviceErrors.findIndex((rowError) => rowError);
            if (firstErrorIndex !== -1) {
                setExpanded(firstErrorIndex);
            }
        }
    };

    const addNewService = () => {
        if (checkAllFilled) {
            const params = {
                startDate: watch(`authStartDate`),
                endDate: watch(`authEndDate`),
                serviceId: null,
                totalUnits: null,
                // apptVisible: true,
                chargeRateIds: [],
                frequencyLimit: {
                    limit: null,
                    mode: null,
                },
            };
            if (item?.authorizationUniqueId) {
                params.authorizationUniqueId = item?.authorizationUniqueId;
            }
            append(params);
        } else {
            // Current service row is missing required fields — expand it and surface the
            // validation errors so the user can see exactly what needs to be filled.
            const lastIndex = fields.length - 1;
            setExpanded(lastIndex);
            trigger(`authorizedServices.${lastIndex}`);
        }
    };

    useEffect(() => {
        if (item) {
            dispatch(httpRequestsOnLoadActions.appendLoading(GET_FS_CHARGE_RATES));

            const fetchChargeRates = async () => {
                const services = Array.from(
                    item?.authorizedServices
                        ?.reduce((map, i) => {
                            const serviceId = i?.service?._id;
                            if (serviceId && !map.has(serviceId)) {
                                map.set(serviceId, {
                                    serviceId,
                                    authorizedServices: i?.id,
                                    authorizationId: i?.authorizationId,
                                });
                            }
                            return map;
                        }, new Map())
                        .values()
                );

                const newChargeRateLists = [];
                for (const service of services) {
                    try {
                        const res = await authService.checkChargeRates({
                            authUniqueId: item?.authorizationUniqueId,
                            renderingProvider: item?.renderingProviderId,
                            fundingServiceId: service.serviceId,
                            clientId: client?.id,
                            payerId: item?.payerId,
                        });

                        newChargeRateLists.push({
                            serviceId: service.serviceId,
                            authorizedServices: service.authorizedServices,
                            chargeRateList: res?.data,
                        });
                    } catch (error) {
                        console.error('Error fetching charge rates:', error);
                    }
                }
                setChargeRateLists((prev) => [...prev, ...newChargeRateLists]);
            };

            const fetchAuthorizedServices = async () => {
                const list = await Promise.all(
                    item?.authorizedServices?.map(async (i) => {
                        return {
                            id: i?.id,
                            startDate: i?.startDate,
                            endDate: i?.endDate,
                            serviceId: i?.service?._id,
                            totalUnits: i?.totalUnits,
                            apptVisible: i?.apptVisible,
                            chargeRateIds: i?.chargeRates?.map((rate) => rate?.id),
                            frequencyLimit: {
                                limit: i?.frequencyLimit?.limit,
                                mode: i?.frequencyLimit?.mode,
                            },
                            ...i,
                        };
                    }) || []
                );
                return list;
            };

            const resetAuthorizedServices = async () => {
                await fetchChargeRates();
                const authorizedServicesList = await fetchAuthorizedServices();

                reset({
                    payerId: item?.payerId,
                    payerName: item?.payerName,
                    renderingProvider: item?.renderingProviderId,
                    authorizationId: item?.authorizationId,
                    authStartDate: item?.startDate,
                    authEndDate: item?.endDate,
                    billable:
                        item?.billable !== undefined
                            ? item?.billable
                            : item?.authorizedServices?.[0]?.billable !== undefined
                              ? item?.authorizedServices?.[0]?.billable
                              : true,
                    authorizedServices: authorizedServicesList,
                });
                setTimeout(() => {
                    dispatch(httpRequestsOnLoadActions.removeLoading(GET_FS_CHARGE_RATES));
                }, 1000);
                // dispatch(httpRequestsOnLoadActions.removeLoading(GET_FS_CHARGE_RATES));
            };
            resetAuthorizedServices();
        }
    }, [item]);

    const updateChargeRates = (index) => {
        const currentAuth = watch('authorizedServices')?.[index];
        const serviceId = currentAuth?.serviceId || currentAuth?.service?._id;
        const currentUsedList = currentAuth?.chargeRates?.length ? currentAuth?.chargeRates : [];

        const changedChargeRates = chargeRatesList?.map((service) => {
            if (service?.serviceId === serviceId) {
                return {
                    ...service,
                    chargeRateList: [...service.chargeRateList, ...currentUsedList],
                };
            } else {
                return service;
            }
        });
        setChargeRateLists(changedChargeRates);
    };
    const handleGetModifiers = (value, index) => {
        if (value && watch('authorizationId')) {
            const checkIfInList = chargeRatesList?.find((i) => i?.serviceId === value?.id);

            if (!checkIfInList) {
                setCheckModifiersLoader(true);
                authService
                    .checkChargeRates(
                        {
                            authUniqueId: item ? item?.authorizationUniqueId : uniqueId,
                            // authId: watch(`authorizationId`),
                            fundingServiceId: value?.id,
                            clientId: client?.id,
                            payerId: watch(`payerId`),
                        }
                        // value?.id, watch(`authorizationId`)
                    )
                    .then((res) => {
                        // const chargeRateList = res?.data?.filter((i) => i?.status);
                        const chargeRateList = res?.data;
                        setChargeRateLists([...chargeRatesList, { serviceId: value?.id, chargeRateList: chargeRateList }]);
                    })
                    .finally(() => {
                        setCheckModifiersLoader(false);
                    });
            }
        } else {
            if (item) {
                updateChargeRates(index);
            }
            setValue(`authorizedServices.${index}.chargeRates`, []);
            setValue(`authorizedServices.${index}.chargeRateIds`, []);
            setValue(`authorizedServices.${index}.serviceId`, null);
        }
    };

    const handleRemoveRow = (index) => {
        updateChargeRates(index);
        remove(index);
    };

    const chargeRatesCurrentList = (index) => {
        const serviceId = watch(`authorizedServices.${index}.serviceId`);
        const list = watch(`authorizedServices.${index}.chargeRates`);
        const currentList = chargeRatesList?.find((i) => i?.serviceId === serviceId);

        if (currentList) {
            const mergedList = item && list ? [...list, ...currentList?.chargeRateList] : currentList?.chargeRateList;
            return [...new Map(mergedList.map((rate) => [rate.id, rate])).values()];
        } else {
            return [];
        }
    };

    if (loadModal?.length) {
        return (
            <div style={{ width: 800 }}>
                <Loader />
            </div>
        );
    }

    return (
        <form style={{ width: 800 }} onSubmit={handleSubmit(handleCreate, handleInvalid)}>
            <div className="flex-align-center" style={{ gap: 8 }}>
                <h1 className="modal-header-title">{clone ? 'Add Authorization' : item ? 'Edit Authorization' : 'Add Authorization'}</h1>

                {item && !item?.billable && <NonBillable />}
            </div>

            <div className="modal-body-wrapper" style={{ marginBottom: 24 }}>
                <div className="flex-align-start" style={{ gap: 16 }}>
                    <div className="full-width">
                        <Input
                            name={'authorizationId'}
                            label="Authorization #*"
                            variant="outlined"
                            control={control}
                            maxLength={MAX_AUTHORIZATION_ID_LENGTH}
                            rules={{ required: watch('authorizationId') ? null : 'Authorization is required.' }}
                            noErrorMessage={true}
                            disabled={watch('authorizationId') === NO_PRIOR}
                        />
                        <div className="flex-align-center" style={{ gap: 8, marginTop: 6 }}>
                            <Checkbox
                                onClick={(e) => {
                                    const value = watch('authorizationId') === NO_PRIOR ? '' : NO_PRIOR;
                                    setValue('authorizationId', value);
                                }}
                                name={'checkBox'}
                                style={{
                                    padding: 0,
                                    color: '#347AF0',
                                }}
                                checked={watch('authorizationId') === NO_PRIOR}
                            />
                            <p className="no-prior-auth">No Prior Auth Required</p>
                        </div>
                    </div>
                    <SelectAutocompleteMultiple
                        name={'payerId'}
                        label={'Payer*'}
                        control={control}
                        options={clientEnrollmentPayers?.map(({ id, payerId, ...rest }) => ({ ...rest, id: payerId }))}
                        rules={{ required: 'Payer is required.' }}
                        renderValue={(option) => option?.payerName}
                        renderCustomRow={(option) => (
                            <div className="flex-align-center" style={{ gap: 4 }}>
                                <div className={option?.terminationDate ? 'circle-box red' : 'circle-box green'} />
                                <TextRow name={option?.payerName} textWidth={7} />
                                {option?.type === enrollmentTypeEnums.PRIMARY && <img src={Images.primaryStar} alt="icon" />}
                            </div>
                        )}
                        handleChange={(value) => {
                            reset({
                                payerId: value?.id ? value?.id : null,
                                payerName: value?.name ? value?.name : null,
                                renderingProvider: watch('renderingProvider'),
                                authorizationId: watch('authorizationId'),
                                authStartDate: watch('authStartDate'),
                                authEndDate: watch('authEndDate'),
                                billable: watch('billable'),
                                authorizedServices: [
                                    {
                                        serviceId: null,
                                        totalUnits: null,
                                        apptVisible: true,
                                        startDate: null,
                                        endDate: null,
                                        chargeRateList: [],
                                        chargeRateIds: [],
                                        frequencyLimit: {
                                            limit: null,
                                            mode: null,
                                        },
                                    },
                                ],
                            });
                            if (!value) {
                                dispatch(fundingSourceActions.removeFoundingSourceServices());
                            }
                        }}
                        defaultSelected={
                            watch('payerName') && {
                                payerName: watch('payerName'),
                                id: watch('payerId'),
                            }
                        }
                    />
                </div>

                <div className="flex-align-center full-width" style={{ gap: 16, margin: '16px 0 0 0' }}>
                    <div className="full-width">
                        <Controller
                            name="renderingProvider"
                            control={control}
                            rules={{ required: 'Rendering Provider is required.' }}
                            render={({ field, fieldState: { error } }) => {
                                return (
                                    <SelectAutocompletePagination
                                        defaultStatuses={[staffStatusEnums.ACTIVE]}
                                        title={'Rendering Provider*'}
                                        name="renderingProvider"
                                        handleSelect={(e) => {
                                            field.onChange(e?.target?.value ? e?.target?.value : null); // update RHF
                                        }}
                                        defaultValue={field.value}
                                        renderValue={(i) => renderStaffName(i) || i?.name}
                                        type="staff"
                                        error={error?.ref?.name}
                                        typeError={error?.message}
                                        currentSelected={
                                            field.value && {
                                                name: item?.renderingProviderName,
                                                id: item?.renderingProviderId,
                                            }
                                        }
                                    />
                                );
                            }}
                        />

                        {/*<SelectAutocompletePagination*/}
                        {/*    defaultStatuses={[staffStatusEnums.ACTIVE]}*/}
                        {/*    title={'Rendering Provider*'}*/}
                        {/*    name={'renderingProvider'}*/}
                        {/*    handleSelect={(e) => {*/}
                        {/*        setValue(`renderingProvider`, e?.target?.value)*/}
                        {/*        clearErrors('renderingProvider');*/}
                        {/*    }}*/}
                        {/*    defaultValue={watch('renderingProvider')}*/}
                        {/*    renderValue={(i) => renderStaffName(i) || i?.name}*/}
                        {/*    type={'staff'}*/}
                        {/*    error={!!errors?.renderingProvider && 'renderingProvider'}*/}
                        {/*    typeError={errors?.renderingProvider?.message}*/}
                        {/*    currentSelected={watch('renderingProvider') && {*/}
                        {/*        name: item?.renderingProviderName,*/}
                        {/*        id: item?.renderingProviderId,*/}
                        {/*    }}*/}
                        {/*/>*/}
                    </div>

                    <div className="flex-align-center full-width" style={{ gap: 16 }}>
                        <DateCalendarForm
                            handleChange={(date) => {
                                if (date) {
                                    watch('authorizedServices')?.forEach((item, index) => {
                                        setValue(`authorizedServices.${index}.startDate`, date);
                                    });
                                }
                            }}
                            name={`authStartDate`}
                            label={'Start Date*'}
                            max={watch('authEndDate') ? moment.utc(watch('authEndDate')).format('YYYY-MM-DD') : ''}
                            control={control}
                            rules={{ required: 'Start date is required.' }}
                        />
                        <DateCalendarForm
                            handleChange={(date) => {
                                if (date) {
                                    watch('authorizedServices')?.forEach((item, index) => {
                                        setValue(`authorizedServices.${index}.endDate`, date);
                                    });
                                }
                            }}
                            name={`authEndDate`}
                            label={'End Date*'}
                            min={watch('authStartDate') ? moment.utc(watch('authStartDate')).format('YYYY-MM-DD') : ''}
                            control={control}
                            rules={{ required: 'End date is required.' }}
                        />
                    </div>
                </div>

                <div className="space-between">
                    <AddCircleBlue
                        styles={{ opacity: checkAllFilled ? 1 : 0.5 }}
                        type="button"
                        handleClick={addNewService}
                        text={'Add Auth Service'}
                    />

                    <div className="flex-align-center" style={{ gap: 24 }}>
                        {!watch('billable') && (
                            <div className="flex-align-center" style={{ gap: 8 }}>
                                <img src={Images.nonBilAble} alt="warning" />
                                <p className="authorization-non-billable">This authorization is non-billable</p>
                            </div>
                        )}

                        <div className="flex-align-center" style={{ gap: 8 }}>
                            <SwitchCustomized name={'billable'} control={control} />
                            <p style={{ fontSize: 13, color: '#475467' }}>Billable</p>
                        </div>
                    </div>
                </div>

                {(clone ? true : watch('authorizationId')) && watch('payerId') && (
                    <div className="auth-service-wrapper">
                        {fields?.map((service, index) => {
                            return (
                                <Accordion
                                    key={service.id}
                                    expanded={expanded === index}
                                    onChange={() => setExpanded(expanded === index ? '' : index)}
                                >
                                    <AccordionSummary>
                                        <p>
                                            {watch(`authorizedServices.${index}.serviceId`)
                                                ? fundingSourceServices.find((i) => i.id === watch(`authorizedServices.${index}.serviceId`))
                                                      ?.serviceTypeId?.name
                                                : `Auth Service`}
                                        </p>

                                        <div className="flex-align-center" style={{ gap: 8 }}>
                                            {watch('authorizedServices')?.length > 1
                                                ? (clone ? true : !item) && (
                                                      <EditWithIcon
                                                          styles={{ opacity: watch('authorizedServices')?.length === 1 ? 0.5 : 1 }}
                                                          disabled={watch('authorizedServices')?.length === 1}
                                                          icon={Images.redTrash}
                                                          type={'button'}
                                                          handleClick={
                                                              () => handleRemoveRow(index)
                                                              // remove(index)
                                                          }
                                                      />
                                                  )
                                                : ''}
                                            <div style={{ width: 24 }}>
                                                <img src={expanded === service.id ? Images.dropUp : Images.dropDown} alt="icon" />
                                            </div>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <SelectAutocompleteMultiple
                                            disabled={!watch('payerId') || !watch('authorizationId')}
                                            handleChange={(value) => handleGetModifiers(value, index)}
                                            loadType={GET_FS_SERVICE}
                                            name={`authorizedServices.${index}.serviceId`}
                                            label={'Service Code*'}
                                            control={control}
                                            options={fundingSourceServices}
                                            rules={{ required: 'Service Code is required.' }}
                                            renderValue={(option) => `${option?.serviceTypeId?.name} ${option?.cptCode}`}
                                            defaultSelected={
                                                item &&
                                                !watch(`authorizedServices.${index}.serviceId`) && {
                                                    serviceTypeId: { name: service?.service?.serviceTypeId?.name },
                                                    cptCode: service?.service?.cptCode,
                                                }
                                            }
                                        />
                                        <Controller
                                            name={`authorizedServices.${index}.chargeRateIds`}
                                            control={control}
                                            rules={{
                                                validate: (value) => value?.length > 0 || 'At least one charge rate must be selected.',
                                            }}
                                            render={({ field, formState: { errors } }) => {
                                                const error = errors?.authorizedServices?.[index]?.chargeRateIds?.message;
                                                return (
                                                    <div className="service-modifiers-container">
                                                        <div className={`display-code-block ${error ? 'error' : ''}`}>
                                                            <p className={classes.displayCodeBlockText}>Charge Rates</p>
                                                            <div
                                                                style={{ margin: 0, gap: '8px' }}
                                                                className={`${classes.availableModfiers} ${checkModifiersLoader === true ? 'notApplicable' : ''}`}
                                                            >
                                                                {checkModifiersLoader ? (
                                                                    <MinLoader
                                                                        color={Colors.ThemeBlue}
                                                                        margin={'0'}
                                                                        position={'relative'}
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        {!!chargeRatesCurrentList(index) ? (
                                                                            chargeRatesCurrentList(index)?.map((item, itemIndex) => {
                                                                                const authorizedServices =
                                                                                    watch('authorizedServices') || [];
                                                                                const selectedRates =
                                                                                    watch(`authorizedServices.${index}.chargeRateIds`) ||
                                                                                    [];
                                                                                const isSelected = selectedRates.includes(item?.id);
                                                                                const usedIds = authorizedServices.reduce(
                                                                                    (acc, i) => [...acc, ...i?.chargeRateIds],
                                                                                    []
                                                                                );
                                                                                const alreadyUsed = usedIds.includes(item?.id);
                                                                                const alreadyUsedForEdit = service?.chargeRates?.find(
                                                                                    (i) => i?.id === item?.id
                                                                                );
                                                                                const checkIfZero = clone
                                                                                    ? true
                                                                                    : service
                                                                                      ? service?.billedUnits === 0 &&
                                                                                        service?.completedUnits === 0 &&
                                                                                        service?.reservedUnits === 0
                                                                                      : true;
                                                                                const canEdit = alreadyUsedForEdit ? !checkIfZero : false;

                                                                                const checkShow = isSelected || !alreadyUsed;

                                                                                return (
                                                                                    checkShow && (
                                                                                        <button
                                                                                            disabled={canEdit}
                                                                                            key={itemIndex}
                                                                                            style={{ margin: 0 }}
                                                                                            className={`${classes.availableModfier} ${isSelected ? 'checked' : ''}`}
                                                                                            onClick={(e) => {
                                                                                                e.preventDefault();
                                                                                                const updatedRates = isSelected
                                                                                                    ? selectedRates.filter(
                                                                                                          (i) => i !== item?.id
                                                                                                      )
                                                                                                    : [...selectedRates, item?.id];
                                                                                                setValue(
                                                                                                    `authorizedServices.${index}.chargeRateIds`,
                                                                                                    updatedRates,
                                                                                                    { shouldValidate: true }
                                                                                                );

                                                                                                if (alreadyUsedForEdit?.id === item?.id) {
                                                                                                    setChargeRateLists(
                                                                                                        (prevChargeRates) => {
                                                                                                            return prevChargeRates.map(
                                                                                                                (service) => {
                                                                                                                    if (
                                                                                                                        service.serviceId ===
                                                                                                                        watch(
                                                                                                                            `authorizedServices.${index}.serviceId`
                                                                                                                        )
                                                                                                                    ) {
                                                                                                                        return {
                                                                                                                            ...service,
                                                                                                                            chargeRateList:
                                                                                                                                [
                                                                                                                                    ...new Map(
                                                                                                                                        [
                                                                                                                                            ...service.chargeRateList,
                                                                                                                                            item,
                                                                                                                                        ].map(
                                                                                                                                            (
                                                                                                                                                rate
                                                                                                                                            ) => [
                                                                                                                                                rate.id,
                                                                                                                                                rate,
                                                                                                                                            ]
                                                                                                                                        )
                                                                                                                                    ).values(),
                                                                                                                                ],
                                                                                                                        };
                                                                                                                    }
                                                                                                                    return service;
                                                                                                                }
                                                                                                            );
                                                                                                        }
                                                                                                    );
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            {item.name}
                                                                                        </button>
                                                                                    )
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <div>
                                                                                <NoYet text="No charge rates available" />
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <ErrMessage
                                                            text={error}
                                                            styles={{
                                                                position: 'relative',
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            }}
                                        />

                                        <div className="flex-align-center" style={{ gap: 16 }}>
                                            <div className="full-width">
                                                <NumericFormatCustom
                                                    name={`authorizedServices.${[index]}.totalUnits`}
                                                    label={'Total Units*'}
                                                    thousandSeparator={false}
                                                    fixedDecimalScale={false}
                                                    control={control}
                                                    rules={{
                                                        required: 'Total Units is required.',
                                                    }}
                                                />
                                                {/*<Input*/}
                                                {/*    type={'number'}*/}
                                                {/*    name={`authorizedServices.${[index]}.totalUnits`}*/}
                                                {/*    label={'Total Units*'}*/}
                                                {/*    variant="outlined"*/}
                                                {/*    control={control}*/}
                                                {/*    rules={{ required: 'Available Units is required.' }}*/}
                                                {/*/>*/}
                                            </div>
                                            <DateCalendarForm
                                                name={`authorizedServices.${index}.startDate`}
                                                label={'Start Date*'}
                                                max={
                                                    watch(`authorizedServices.${index}.endDate`)
                                                        ? moment.utc(watch(`authorizedServices.${index}.endDate`)).format('YYYY-MM-DD')
                                                        : ''
                                                }
                                                control={control}
                                                rules={{ required: 'Start date is required.' }}
                                            />
                                            <DateCalendarForm
                                                name={`authorizedServices.${index}.endDate`}
                                                label={'End Date*'}
                                                min={
                                                    watch(`authorizedServices.${index}.startDate`)
                                                        ? moment.utc(watch(`authorizedServices.${index}.startDate`)).format('YYYY-MM-DD')
                                                        : ''
                                                }
                                                control={control}
                                                rules={{ required: 'End date is required.' }}
                                            />
                                        </div>

                                        <Controller
                                            name={`authorizedServices.${index}.frequencyLimit.mode`}
                                            control={control}
                                            rules={{ required: 'Frequency is required.' }}
                                            render={({ field, formState: { errors } }) => {
                                                const error =
                                                    errors?.authorizedServices?.[index]?.frequencyLimit?.mode?.message ===
                                                    'Frequency is required.';
                                                return (
                                                    <>
                                                        <div
                                                            style={error ? { border: '1px solid #FE7070' } : {}}
                                                            className="frequency-wrapper"
                                                        >
                                                            <p className="frequency-title">Set Frequency Limits</p>
                                                            <div className="authorization-frequency-box">
                                                                {frequencyList.map((i, j) => (
                                                                    <div
                                                                        style={{ width: i?.count ? '35%' : '30%' }}
                                                                        className="frequency-box"
                                                                        key={j}
                                                                    >
                                                                        <FormControlLabel
                                                                            label={i?.title}
                                                                            control={
                                                                                <Radio
                                                                                    {...field}
                                                                                    value={i?.value}
                                                                                    checked={field.value === i?.value}
                                                                                    style={{
                                                                                        color: error
                                                                                            ? '#FE7070'
                                                                                            : watch(
                                                                                                    `authorizedServices.${index}.frequencyLimit.mode`
                                                                                                ) === i?.value
                                                                                              ? '#347AF0'
                                                                                              : '#D0D5DD',
                                                                                    }}
                                                                                    onChange={(e) => field.onChange(e.target.value)}
                                                                                />
                                                                            }
                                                                        />
                                                                        {watch(`authorizedServices.${index}.frequencyLimit.mode`) ===
                                                                            i?.value &&
                                                                            watch(`authorizedServices.${index}.frequencyLimit.mode`) !==
                                                                                frequencyEnumsConvert?.ENTIRE_TERM && (
                                                                                <>
                                                                                    <p>Unit Limit</p>
                                                                                    <Input
                                                                                        type={'number'}
                                                                                        name={`authorizedServices.${[index]}.frequencyLimit.limit`}
                                                                                        variant="outlined"
                                                                                        control={control}
                                                                                        rules={{ required: 'Limit is required.' }}
                                                                                    />
                                                                                </>
                                                                            )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            }}
                                        />

                                        <div className="appt-visible-switcher-wrapper">
                                            <SwitchCustomized name={`authorizedServices.${index}.apptVisible`} control={control} />
                                            <p>Appt visible</p>
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </div>
                )}
            </div>
            <CreateChancel
                loader={loader?.length}
                type="submit"
                create={clone ? 'Add' : item ? 'Save' : 'Add'}
                chancel={'Cancel'}
                buttonWidth="100%"
                onClose={close}
            />
        </form>
    );
};
